/**
 * Client-side catalog search for the bottom-nav filter.
 *
 * On the home route, typing filters the existing home gallery Packery items
 * in place. Off home, submitting the search navigates to the home route with
 * the query so that page can apply the same filter.
 *
 * Must load before alpine.min.js.
 */

/**
 * @typedef {Object} SearchProduct
 * @property {string} title
 * @property {string} url
 * @property {string | null} image
 * @property {number | null} width
 * @property {number | null} height
 * @property {string[]} categories
 */

/**
 * @param {string} query
 * @returns {string[]}
 */
function queryTokens(query) {
  return String(query || "")
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
}

/**
 * @param {SearchProduct[]} products
 * @param {string} query
 * @returns {SearchProduct[]}
 */
function filterProducts(products, query) {
  const tokens = queryTokens(query);

  if (!tokens.length || !Array.isArray(products)) return [];

  return products.filter((product) => {
    const haystack = [product?.title, ...(product?.categories || [])]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return tokens.every((token) => haystack.includes(token));
  });
}

/**
 * @returns {SearchProduct[]}
 */
function readCatalog() {
  const node = document.getElementById("ProductSearchCatalog");
  if (!node) return [];

  try {
    const parsed = JSON.parse(node.textContent || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error(error);
    return [];
  }
}

/**
 * @param {SearchProduct[]} products
 * @returns {Map<string, SearchProduct>}
 */
function catalogByUrl(products) {
  /** @type {Map<string, SearchProduct>} */
  const map = new Map();
  for (const product of products) {
    if (product?.url) map.set(product.url, product);
  }
  return map;
}

function searchDialog() {
  const dialog = document.getElementById("product-search");
  return dialog instanceof HTMLDialogElement ? dialog : null;
}

/**
 * @returns {HTMLElement | null}
 */
function homeSearchLayout() {
  return document.querySelector("packery-layout[data-home-search]");
}

/**
 * @param {string} query
 * @param {Map<string, SearchProduct>} productsByUrl
 * @returns {Promise<void>}
 */
async function applyHomeGalleryFilter(query, productsByUrl) {
  const layout = homeSearchLayout();
  if (!layout) return;

  const tokens = queryTokens(query);
  if (tokens.length && typeof layout.loadAll === "function") {
    await layout.loadAll();
  }

  const currentQuery =
    (typeof Alpine !== "undefined" && Alpine.store("productSearch")?.query) ||
    query;
  const currentTokens = queryTokens(currentQuery);
  const items = layout.querySelectorAll("[data-packery-item]");

  for (const item of items) {
    if (!(item instanceof HTMLElement)) continue;

    const link = item.querySelector("[data-url], [data-title]");
    const title = link instanceof HTMLElement ? link.dataset.title || "" : "";
    const url = link instanceof HTMLElement ? link.dataset.url || "" : "";
    const product = url ? productsByUrl.get(url) : undefined;
    const haystack = [title, ...(product?.categories || [])]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    const match =
      !currentTokens.length ||
      currentTokens.every((token) => haystack.includes(token));

    item.hidden = !match;
  }

  if (typeof layout.refresh === "function") layout.refresh();
}

/**
 * @param {string} query
 */
function syncHomeSearchUrl(query) {
  if (!window.theme?.isHome) return;

  const url = new URL(window.location.href);
  const trimmed = String(query || "").trim();

  if (trimmed) url.searchParams.set("q", trimmed);
  else url.searchParams.delete("q");

  const next = `${url.pathname}${url.search}${url.hash}`;
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (next !== current) {
    window.history.replaceState(window.history.state, "", next);
  }
}

/**
 * @returns {string}
 */
function initialHomeQuery() {
  if (!window.theme?.isHome) return "";
  return new URLSearchParams(window.location.search).get("q") || "";
}

/**
 * @param {string} query
 * @returns {string}
 */
function homeSearchHref(query) {
  const root = window.theme?.routes?.rootUrl || "/";
  const url = new URL(root, window.location.origin);
  const trimmed = String(query || "").trim();
  if (trimmed) url.searchParams.set("q", trimmed);
  return `${url.pathname}${url.search}`;
}

if (typeof document !== "undefined") {
  document.addEventListener("alpine:init", () => {
    const products = readCatalog();
    const productsByUrl = catalogByUrl(products);
    const startingQuery = initialHomeQuery();

    Alpine.store("productSearch", {
      open: false,
      inlineOpen: Boolean(startingQuery && window.theme?.isHome),
      query: startingQuery,
      isHome: Boolean(window.theme?.isHome),

      get filtering() {
        return this.query.trim().length > 0;
      },

      openPanel() {
        const dialog = searchDialog();
        if (!dialog) return;

        this.inlineOpen = false;
        this.open = true;
        if (!dialog.open) dialog.show();
        dialog.querySelector("input")?.focus();
      },

      openInline() {
        this.open = false;
        this.inlineOpen = true;
        searchDialog()?.close();
      },

      closeInline() {
        if (!this.inlineOpen) return;

        this.inlineOpen = false;
        this.query = "";
      },

      clear() {
        this.query = "";
        this.open = false;
        this.inlineOpen = false;
        searchDialog()?.close();
      },

      handleClose() {
        // Keep the query so closing the mobile panel still leaves the home
        // gallery filtered. Explicit clear / inline close wipe the query.
        this.open = false;
      },

      submit() {
        if (this.isHome) {
          this.open = false;
          searchDialog()?.close();
          return;
        }

        window.location.assign(homeSearchHref(this.query));
      },
    });
  });

  document.addEventListener("alpine:initialized", () => {
    const store = Alpine.store("productSearch");
    if (!store) return;

    const products = readCatalog();
    const productsByUrl = catalogByUrl(products);

    Alpine.effect(() => {
      const query = store.query;
      if (!store.isHome) return;

      syncHomeSearchUrl(query);
      applyHomeGalleryFilter(query, productsByUrl);
    });
  });
}

if (typeof module !== "undefined" && module.exports) {
  module.exports = { filterProducts, queryTokens, applyHomeGalleryFilter };
}
